import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ShieldAlert, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';

export function ContentFilterLog() {
  const { data: incidents, isLoading, refetch } = useQuery({
    queryKey: ['content-filter-incidents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('signature_extraction_logs')
        .select('id, created_at, sender_email, user_email, user_id, error_message, is_test_mail')
        .like('error_message', 'CONTENT_FILTER%')
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data || [];
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            <div>
              <CardTitle>Content Filter Incidents</CardTitle>
              <CardDescription>
                Azure content filter blocks (false positives) — emails that could not be processed
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-destructive border-destructive">
              {incidents?.length ?? 0} incidents
            </Badge>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : !incidents?.length ? (
          <p className="text-muted-foreground text-sm">No content filter incidents recorded.</p>
        ) : (
          <div className="rounded-md border overflow-auto max-h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Sender</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Test?</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incidents.map((inc) => (
                  <TableRow key={inc.id}>
                    <TableCell className="whitespace-nowrap text-sm">
                      {inc.created_at ? format(new Date(inc.created_at), 'dd.MM.yyyy HH:mm:ss') : '-'}
                    </TableCell>
                    <TableCell className="text-sm font-mono">
                      {inc.sender_email || '-'}
                    </TableCell>
                    <TableCell className="text-sm font-mono">
                      {inc.user_email || '-'}
                    </TableCell>
                    <TableCell>
                      {inc.is_test_mail ? (
                        <Badge variant="secondary">Test</Badge>
                      ) : (
                        <Badge variant="outline">Live</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-destructive max-w-xs truncate">
                      {inc.error_message}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
